<script setup lang="ts">
interface FaqItem {
  question: string;
  answer: string;
}

interface FaqBlock {
  title: string;
  intro?: string;
  items: FaqItem[];
}

defineProps<{
  block: FaqBlock;
}>();

const { t } = useI18n();

const openId = ref<number | null>(null);

function toggle(i: number) {
  openId.value = openId.value === i ? null : i;
}
</script>

<template>
  <section
    class="relative overflow-hidden bg-white dark:bg-slate-900"
    aria-labelledby="faq-accordion-heading"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -left-16 h-80 w-80 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -right-16 h-80 w-80 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
    />

    <div class="relative mx-auto w-full max-w-4xl px-4 py-8 md:py-12">
      <header data-reveal class="mb-6 text-center md:mb-8">
        <div
          class="flex items-center justify-center gap-3 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500"
        >
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
          <span>{{ t("location.faq.eyebrow") }}</span>
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
        </div>
        <h2
          id="faq-accordion-heading"
          class="mt-4 font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ block.title }}
        </h2>
        <p
          v-if="block.intro"
          class="mx-auto mt-5 max-w-2xl font-body text-base text-slate-600 md:text-lg dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <ul data-reveal class="divide-y divide-slate-200 dark:divide-slate-700/60">
        <li
          v-for="(item, i) in block.items"
          :key="i"
          class="group"
        >
          <button
            type="button"
            class="flex w-full items-start gap-4 py-5 text-left transition-colors md:py-6"
            :aria-expanded="openId === i"
            :aria-controls="`faq-answer-${i}`"
            @click="toggle(i)"
          >
            <Icon
              name="tabler:help-circle"
              size="22"
              class="mt-1 shrink-0 text-amber-500 dark:text-amber-400"
              aria-hidden="true"
            />
            <span
              class="flex-1 font-display text-lg italic leading-snug text-slate-900 sm:text-xl md:text-2xl dark:text-white"
            >
              {{ item.question }}
            </span>
            <span
              class="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 transition-all duration-300 group-hover:bg-amber-400/10 group-hover:text-amber-600 dark:text-slate-400 dark:group-hover:text-amber-400"
              :class="{ 'rotate-45 text-amber-600 dark:text-amber-400': openId === i }"
              aria-hidden="true"
            >
              <Icon name="tabler:plus" size="22" />
            </span>
          </button>

          <Transition
            enter-active-class="grid transition-all duration-300 ease-out"
            enter-from-class="grid-rows-[0fr] opacity-0"
            enter-to-class="grid-rows-[1fr] opacity-100"
            leave-active-class="grid transition-all duration-200 ease-in"
            leave-from-class="grid-rows-[1fr] opacity-100"
            leave-to-class="grid-rows-[0fr] opacity-0"
          >
            <div
              v-if="openId === i"
              :id="`faq-answer-${i}`"
              class="overflow-hidden"
            >
              <p
                class="max-w-prose pb-5 pl-10 pr-8 font-body text-base leading-relaxed text-slate-600 md:pb-6 md:text-lg dark:text-slate-300"
              >
                {{ item.answer }}
              </p>
            </div>
          </Transition>
        </li>
      </ul>
    </div>
  </section>
</template>
